import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { useAttributeSumBarChart } from "./hooks/useAttributeSumBarChart";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useGlobalFilters } from "../../../stores/GlobalFilterContext/GlobalFilterContext";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, {
  TileChartContent,
  TileStatusContent,
} from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import AttributeSumBarChartSettings from "./AttributeSumBarChartSettings";
import * as Tooltip from "@radix-ui/react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

const BLUE_BG = "rgba(59, 130, 246, 0.7)";
const BLUE_BORDER = "rgba(59, 130, 246, 1)";
const GRAY_BG = "rgba(209, 213, 219, 0.7)";
const GRAY_BORDER = "rgba(209, 213, 219, 1)";

let _hatchPattern = null;
function getHatchPattern() {
  if (_hatchPattern) return _hatchPattern;
  if (typeof document === "undefined") return null;
  const size = 8;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "rgba(229, 231, 235, 0.5)";
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = "rgba(107, 114, 128, 0.8)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(0, size);
  ctx.lineTo(size, 0);
  ctx.stroke();
  _hatchPattern = ctx.createPattern(canvas, "repeat");
  return _hatchPattern;
}

function fmtValue(n) {
  const num = Number(n || 0);
  const dec = Number.isInteger(num) ? 0 : 2;
  return num.toLocaleString("de-DE", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

const GRANULARITY_LABELS = {
  day: "Day",
  week: "Week",
  month: "Month",
  year: "Year",
};

function formatXAxisDateLabel(value, granularity) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  if (granularity === "year") {
    return parsed.toLocaleDateString("de-DE", { month: "short" });
  }
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
  });
}

function formatGermanDateLabel(value) {
  const parsed = new Date(value + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function computeDisplayRange(dateStr, gran) {
  if (!dateStr) return { startDate: "", endDate: "" };
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return { startDate: "", endDate: "" };
  const fmt = (dt) => dt.toISOString().slice(0, 10);

  if (gran === "day") return { startDate: fmt(d), endDate: fmt(d) };

  if (gran === "week") {
    const day = d.getDay();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((day + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { startDate: fmt(mon), endDate: fmt(sun) };
  }

  if (gran === "month") {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { startDate: fmt(start), endDate: fmt(end) };
  }

  return {
    startDate: `${d.getFullYear()}-01-01`,
    endDate: `${d.getFullYear()}-12-31`,
  };
}

function defaultAnchor(gran) {
  const today = new Date();
  if (gran === "day") {
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    return y.toISOString().slice(0, 10);
  }
  return today.toISOString().slice(0, 10);
}

/** Isolated date input — uncontrolled to prevent Chrome's date-section cursor from
 *  resetting on every React re-render.
 *
 *  Chrome fires native 'change' on each section auto-advance (month→day→year) during
 *  keyboard typing, NOT only when the picker closes. We distinguish the two paths via
 *  a pointer flag: if the last interaction was a pointer event (click = calendar picker),
 *  commit on 'change'; if it was a key event (keyboard typing), defer to Enter. */
const DateInput = React.memo(function DateInput({ value, onChange, disabled }) {
  const committed = useRef(value);
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const isPointerRef = useRef(false);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Sync from parent when value changes externally (e.g. granularity button click)
  useEffect(() => {
    if (inputRef.current) inputRef.current.value = value;
    committed.current = value;
  }, [value]);

  const commit = useCallback(() => {
    const val = inputRef.current?.value;
    if (!val || val === committed.current) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val) || isNaN(new Date(val + "T00:00:00").getTime())) return;
    committed.current = val;
    onChangeRef.current(val);
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const onPointerDown = () => { isPointerRef.current = true; };
    const onKeyDown = () => { isPointerRef.current = false; };
    const onNativeChange = () => {
      if (isPointerRef.current) {
        isPointerRef.current = false;
        commit();
      }
    };
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('change', onNativeChange);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('change', onNativeChange);
    };
  }, [commit]);

  return (
    <input
      ref={inputRef}
      type="date"
      defaultValue={value}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
      onBlur={commit}
      disabled={disabled}
      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
    />
  );
});

function AttributeSumBarChart({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) {
  const { t } = useTranslation();
  const {
    loading,
    error,
    chartData,
    datasetLastDate,
    objectTypes,
    eventTypes,
    objectTypeNumericAttributes,
    eventTypeNumericAttributes,
    isMetadataLoaded,
    // TileConfig
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
    // ViewState
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    // TileConfig setters
    setTitle,
    setAttributeSource,
    setSelectedObjectTypes,
    setSelectedObjectAttributes,
    setSelectedEventTypes,
    setSelectedEventAttributes,
    setEnabledGranularities,
    // ViewState setters
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    // Actions
    fetchMetadata,
    fetchChartData,
    fetchNumericAttributesForObjectTypes,
  } = useAttributeSumBarChart(activeFile, initialSettings);

  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      title, attributeSource, selectedObjectTypes, selectedObjectAttributes,
      selectedEventTypes, selectedEventAttributes, enabledGranularities,
      granularity, selectedDate, viewMode, dateRangeStart, dateRangeEnd,
    }));
  }, [title, attributeSource, selectedObjectTypes, selectedObjectAttributes,
      selectedEventTypes, selectedEventAttributes, enabledGranularities,
      granularity, selectedDate, viewMode, dateRangeStart, dateRangeEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved title.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(title || "Attribute Sum Bar Chart");
  }, [title]);

  const { filterState } = useGlobalFilters();
  const isFirstFilterRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    fetchMetadata();
    if (hasConfig) {
      fetchChartData();
    }
  }, [filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [draftSettings, setDraftSettings] = useState(null);
  const [viewDateError, setViewDateError] = useState("");

  // Ref to track latest tile config so handleOpenSettings always reads fresh values
  // (the header actions button is stored in a parent ref and may hold a stale closure)
  const tileConfigRef = useRef({
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
  });
  useEffect(() => {
    tileConfigRef.current = {
      title,
      attributeSource,
      selectedObjectTypes,
      selectedObjectAttributes,
      selectedEventTypes,
      selectedEventAttributes,
      enabledGranularities,
    };
  }, [
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
  ]);

  const displayRange = useMemo(
    () => computeDisplayRange(selectedDate, granularity),
    [selectedDate, granularity],
  );

  // --- Check if configured ---
  const hasConfig = useMemo(() => {
    if (attributeSource === "object") {
      return (
        selectedObjectTypes.length > 0 && selectedObjectAttributes.length > 0
      );
    }
    return selectedEventTypes.length > 0 && selectedEventAttributes.length > 0;
  }, [
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
  ]);

  // --- Settings modal handlers ---

  const handleOpenSettings = useCallback(() => {
    const cfg = tileConfigRef.current;
    setDraftSettings({
      title: cfg.title,
      attributeSource: cfg.attributeSource,
      selectedObjectTypes: [...cfg.selectedObjectTypes],
      selectedObjectAttributes: [...cfg.selectedObjectAttributes],
      selectedEventTypes: [...cfg.selectedEventTypes],
      selectedEventAttributes: [...cfg.selectedEventAttributes],
      enabledGranularities: [...cfg.enabledGranularities],
    });
    setIsSettingsModalOpen(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsModalOpen(false);
    setDraftSettings(null);
  }, []);

  const handleApplySettings = useCallback(async () => {
    if (!draftSettings) return;

    const normalizedGranularity = draftSettings.enabledGranularities.includes(
      granularity,
    )
      ? granularity
      : draftSettings.enabledGranularities[0] || "day";

    setIsSettingsModalOpen(false);
    setDraftSettings(null);

    if (onTitleChange) onTitleChange(draftSettings.title);

    setTitle(draftSettings.title);
    setAttributeSource(draftSettings.attributeSource);
    setSelectedObjectTypes(draftSettings.selectedObjectTypes);
    setSelectedObjectAttributes(draftSettings.selectedObjectAttributes);
    setSelectedEventTypes(draftSettings.selectedEventTypes);
    setSelectedEventAttributes(draftSettings.selectedEventAttributes);
    setEnabledGranularities(draftSettings.enabledGranularities);
    setGranularity(normalizedGranularity);

    await fetchChartData({
      ...draftSettings,
      granularity: normalizedGranularity,
      selectedDate,
    });
  }, [
    draftSettings,
    fetchChartData,
    granularity,
    selectedDate,
    onTitleChange,
    setTitle,
    setAttributeSource,
    setSelectedObjectTypes,
    setSelectedObjectAttributes,
    setSelectedEventTypes,
    setSelectedEventAttributes,
    setEnabledGranularities,
    setGranularity,
  ]);

  // --- Auto-load & refresh ---

  useAutoLoad(fetchMetadata, [activeFile], isMetadataLoaded);

  // Use ref so the refresh callback stored in parent always calls latest fetchChartData
  const fetchChartDataRef = useRef(fetchChartData);
  useEffect(() => {
    fetchChartDataRef.current = fetchChartData;
  }, [fetchChartData]);

  const hasConfigRef = useRef(hasConfig);
  useEffect(() => {
    hasConfigRef.current = hasConfig;
  }, [hasConfig]);

  useEffect(() => {
    if (onRefresh) {
      onRefresh(async () => {
        if (isMetadataLoaded && hasConfigRef.current)
          await fetchChartDataRef.current();
      });
    }
  }, [onRefresh, isMetadataLoaded]);

  // --- Header ---

  const headerActions = useMemo(
    () => (
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label={t('common:actions.openSettings')}
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    ),
    [handleOpenSettings],
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  // --- View Mode handlers ---

  const triggerAutoApply = useCallback(
    (overrides = {}) => {
      const effectiveViewMode = overrides.viewMode ?? viewMode;
      const effectiveDateRangeStart =
        overrides.dateRangeStart ?? dateRangeStart;
      const effectiveDateRangeEnd = overrides.dateRangeEnd ?? dateRangeEnd;

      if (effectiveViewMode === "daterange") {
        if (!effectiveDateRangeStart || !effectiveDateRangeEnd) {
          setViewDateError("Select start and end dates.");
          return false;
        }
        if (effectiveDateRangeStart > effectiveDateRangeEnd) {
          setViewDateError("Start date must be on or before end date.");
          return false;
        }
      }
      setViewDateError("");

      fetchChartData({
        viewMode: effectiveViewMode,
        dateRangeStart: effectiveDateRangeStart,
        dateRangeEnd: effectiveDateRangeEnd,
        ...overrides,
      });
      return true;
    },
    [fetchChartData, viewMode, dateRangeStart, dateRangeEnd],
  );

  const handleGranularityChange = useCallback(
    (g) => {
      setGranularity(g);
      const baseAnchor = selectedDate || defaultAnchor(g);
      const normalizedAnchor =
        datasetLastDate && baseAnchor > datasetLastDate
          ? datasetLastDate
          : baseAnchor;
      setSelectedDate(normalizedAnchor);
      triggerAutoApply({ granularity: g, selectedDate: normalizedAnchor });
    },
    [setGranularity, setSelectedDate, selectedDate, datasetLastDate, triggerAutoApply],
  );

  const handleViewModeChange = useCallback(
    (nextMode) => {
      if (nextMode === viewMode) return;
      if (nextMode === "daterange") {
        // Only sync from the last fetched period; never derive from granularity
        if (chartData?.period_start && chartData?.period_end) {
          setDateRangeStart(chartData.period_start);
          setDateRangeEnd(chartData.period_end);
        }
        // Otherwise leave dateRangeStart/End as-is (their own independent state)
      } else {
        const baseAnchor =
          dateRangeStart || selectedDate || defaultAnchor(granularity);
        const normalizedAnchor =
          datasetLastDate && baseAnchor > datasetLastDate
            ? datasetLastDate
            : baseAnchor;
        setSelectedDate(normalizedAnchor);
      }
      setViewMode(nextMode);
    },
    [
      viewMode,
      selectedDate,
      chartData,
      dateRangeStart,
      datasetLastDate,
      setDateRangeStart,
      setDateRangeEnd,
      setSelectedDate,
      setViewMode,
    ],
  );

  const handleSelectedDateCommit = useCallback(
    (nextDate) => {
      setSelectedDate(nextDate);
      if (viewMode === "granularity") {
        triggerAutoApply({ viewMode: "granularity", selectedDate: nextDate });
      }
    },
    [setSelectedDate, viewMode, triggerAutoApply],
  );

  const handleDateRangeStartCommit = useCallback(
    (nextDate) => {
      setDateRangeStart(nextDate);
      setViewDateError("");
    },
    [setDateRangeStart],
  );

  const handleDateRangeEndCommit = useCallback(
    (nextDate) => {
      setDateRangeEnd(nextDate);
      setViewDateError("");
    },
    [setDateRangeEnd],
  );

  const handleApplyDateRange = useCallback(() => {
    triggerAutoApply({ viewMode: "daterange" });
  }, [triggerAutoApply]);

  const handleDateRangePresetChange = useCallback(
    (value) => {
      const start = (value?.startDate || "").slice(0, 10);
      const end = (value?.endDate || "").slice(0, 10);
      setDateRangeStart(start);
      setDateRangeEnd(end);
      setViewMode("daterange");
      setViewDateError("");
      triggerAutoApply({ viewMode: "daterange", dateRangeStart: start, dateRangeEnd: end });
    },
    [setDateRangeStart, setDateRangeEnd, setViewMode, triggerAutoApply],
  );

  // --- Chart data ---

  const items = chartData?.chart_items || [];

  const chartjsData = useMemo(() => {
    if (!items.length) return null;

    const maxReal = Math.max(
      ...items.filter((d) => d.item_count > 0).map((d) => d.total_value),
      0,
    );
    const placeholderHeight = Math.max(maxReal, 1);
    const hatchPattern = getHatchPattern();

    const labels = [];
    const values = [];
    const backgrounds = [];
    const borders = [];
    const noDataFlags = [];

    const labelGranularity = viewMode === "daterange" ? "day" : granularity;
    items.forEach((d) => {
      labels.push(formatXAxisDateLabel(d.bucket_label, labelGranularity));
      if (d.item_count === 0) {
        values.push(placeholderHeight);
        backgrounds.push(hatchPattern || GRAY_BG);
        borders.push(GRAY_BORDER);
        noDataFlags.push(true);
      } else {
        values.push(d.total_value);
        backgrounds.push(BLUE_BG);
        borders.push(BLUE_BORDER);
        noDataFlags.push(false);
      }
    });

    return {
      labels,
      datasets: [{
        label: "Value",
        data: values,
        backgroundColor: backgrounds,
        borderColor: borders,
        borderWidth: 1,
        borderRadius: (ctx) => noDataFlags[ctx.dataIndex] ? 0 : 6,
        barPercentage: 0.7,
        categoryPercentage: 0.8,
      }],
    };
  }, [items, granularity, viewMode]);

  const hasEmptyBuckets = useMemo(
    () => items.some((d) => d.item_count === 0),
    [items],
  );

  const chartjsOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: hasEmptyBuckets,
        position: "top",
        labels: {
          generateLabels: () => [
            { text: t('tiles:attributeSum.axisValue'), fillStyle: BLUE_BG, strokeStyle: BLUE_BORDER, lineWidth: 1 },
            { text: "No data", fillStyle: getHatchPattern() || GRAY_BG, strokeStyle: GRAY_BORDER, lineWidth: 1 },
          ],
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const d = items[context.dataIndex];
            if (!d) return "";
            if (d.item_count === 0) return "No data";
            const sourceLabel = chartData?.attribute_source === "event" ? "Events" : "Objects";
            return [`Sum: ${fmtValue(d.total_value)}`, `${sourceLabel}: ${d.item_count}`];
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: t('common:chart.date'), color: "#4b5563", font: { size: 12 } },
        ticks: { maxRotation: 45, color: "#6b7280", font: { size: 11 } },
      },
      y: {
        title: { display: true, text: t('tiles:attributeSum.legendSum'), color: "#4b5563", font: { size: 12 } },
        ticks: { color: "#6b7280", font: { size: 11 } },
        beginAtZero: true,
      },
    },
  }), [hasEmptyBuckets, items, chartData]);

  // --- Derived state ---

  const canCalculate = useMemo(() => {
    if (!isMetadataLoaded) return false;
    if (!selectedDate && !draftSettings) return false;
    const draft = draftSettings || {
      attributeSource,
      selectedObjectTypes,
      selectedObjectAttributes,
      selectedEventTypes,
      selectedEventAttributes,
      enabledGranularities,
    };

    if (!draft.enabledGranularities?.length) return false;
    if (draft.attributeSource === "object") {
      return (
        (draft.selectedObjectTypes?.length ?? 0) > 0 &&
        (draft.selectedObjectAttributes?.length ?? 0) > 0
      );
    }
    return (
      (draft.selectedEventTypes?.length ?? 0) > 0 &&
      (draft.selectedEventAttributes?.length ?? 0) > 0
    );
  }, [
    isMetadataLoaded,
    selectedDate,
    draftSettings,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
  ]);

  const showLoadDataPrompt = !isMetadataLoaded && !loading && !error;
  const showConfigPrompt = isMetadataLoaded && !loading && !error && !hasConfig;
  const showContent = isMetadataLoaded && !error && hasConfig;
  const showChart = showContent && items.length > 0;
  const showNoData =
    showContent && !loading && chartData !== null && items.length === 0;
  const showSelectionPrompt = showContent && !loading && chartData === null;

  useEffect(() => {
    if (!showContent || loading || error || chartData !== null) return;
    if (viewMode !== "granularity") return;
    triggerAutoApply();
  }, [showContent, loading, error, chartData, viewMode, triggerAutoApply]);

  return (
    <BaseTile title={title} loading={loading} minHeight="lg" className="h-full">
      <TileContent layout="flex" direction="column" gap="md" grow>
        {showLoadDataPrompt && (
          <TileStatusContent
            status="info"
            title="Load Metadata"
            message="Load metadata first to configure attribute types and selections."
            action={
              <button
                onClick={fetchMetadata}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Load Metadata
              </button>
            }
          />
        )}

        {showConfigPrompt && (
          <TileStatusContent
            status="empty"
            title="Configure Chart"
            message="Open settings to configure attribute source, types, and attributes."
            action={
              <button
                onClick={handleOpenSettings}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Configure Chart
              </button>
            }
          />
        )}

        {showContent && (
          <>
            {/* View Mode Controls */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      Time Period
                    </label>
                    <Tooltip.Provider delayDuration={200}>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <button
                            type="button"
                            className="inline-flex text-gray-400 hover:text-gray-500 transition-colors"
                            aria-label="Time period help"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side="top"
                            align="start"
                            sideOffset={6}
                            className="max-w-[220px] bg-white border border-gray-200 rounded-md shadow-lg z-50"
                          >
                            <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
                              <span className="text-xs font-medium text-primary-700">
                                {viewMode === "granularity"
                                  ? "Granularity Mode"
                                  : "Date Range Mode"}
                              </span>
                            </div>
                            <div className="px-2.5 py-1.5 text-xs leading-normal text-gray-500">
                              {viewMode === "granularity" ? (
                                <>
                                  Pick a granularity (
                                  <span className="text-primary-600 font-medium">
                                    Day, Week, Month, Year
                                  </span>
                                  ) and select a date. Results are calculated
                                  for the full calendar period.
                                </>
                              ) : (
                                <>
                                  Select an explicit{" "}
                                  <span className="text-primary-600 font-medium">
                                    start
                                  </span>{" "}
                                  and{" "}
                                  <span className="text-primary-600 font-medium">
                                    end date
                                  </span>
                                  . Click Apply to update the chart.
                                </>
                              )}
                            </div>
                            <Tooltip.Arrow className="fill-white" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  </div>
                </div>
                <div className="flex gap-0.5 bg-gray-200 rounded-md p-0.5">
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("granularity")}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "granularity"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Granularity
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("daterange")}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "daterange"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Date Range
                  </button>
                </div>
              </div>

              {viewMode === "granularity" ? (
                <div className="flex flex-wrap items-center gap-2">
                  {enabledGranularities.map((g) => {
                    const active = granularity === g;
                    return (
                      <button
                        key={g}
                        type="button"
                        onClick={() => handleGranularityChange(g)}
                        className={`px-3 py-1.5 rounded-md border text-sm ${
                          active
                            ? "bg-primary-600 text-white border-primary-600"
                            : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        {GRANULARITY_LABELS[g] || g}
                      </button>
                    );
                  })}
                  <div className="flex items-center gap-3">
                    <DateInput
                      value={selectedDate}
                      onChange={handleSelectedDateCommit}
                      disabled={loading}
                    />
                    <span className="text-xs text-gray-500">
                      {chartData?.period_label ||
                        `${formatGermanDateLabel(displayRange.startDate)} - ${formatGermanDateLabel(displayRange.endDate)}`}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <DateInput
                      value={dateRangeStart}
                      onChange={handleDateRangeStartCommit}
                      disabled={loading}
                    />
                    <span className="text-xs text-gray-500">to</span>
                    <DateInput
                      value={dateRangeEnd}
                      onChange={handleDateRangeEndCommit}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={handleApplyDateRange}
                      disabled={loading}
                      className="px-4 py-1.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? "Loading..." : "Apply"}
                    </button>
                  </div>
                  {viewDateError && (
                    <p className="text-xs text-error-600">{viewDateError}</p>
                  )}
                </div>
              )}
            </div>

            {showChart && (
              <>
                <TileChartContent height={440} className="flex-grow">
                  <div style={{ position: "relative", width: "100%", height: "100%" }}>
                    <Bar data={chartjsData} options={chartjsOptions} />
                  </div>
                </TileChartContent>
              </>
            )}

            {showNoData && (
              <TileStatusContent
                status="empty"
                title="No Data Available"
                message="No attribute data found for the selected configuration and time period."
                icon={
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                }
              />
            )}

            {showSelectionPrompt && (
              <TileStatusContent
                status="empty"
                title="Select Filters to Load Chart"
                message={
                  viewMode === "granularity"
                    ? "Granularity mode applies automatically: select a granularity and date."
                    : "Date Range mode requires clicking Apply after selecting dates."
                }
              />
            )}
          </>
        )}

        {error && (
          <TileStatusContent
            status="error"
            title="Failed to load chart"
            message={typeof error === "string" ? error : "Unexpected error."}
            action={
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (isMetadataLoaded && hasConfig) fetchChartData();
                    else fetchMetadata();
                  }}
                  className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
                >
                  Retry
                </button>
                <button
                  onClick={handleOpenSettings}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                >
                  Open Settings
                </button>
              </div>
            }
          />
        )}
      </TileContent>

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={(v) => { if (!v) handleCloseSettings(); }}
        getInitialValues={() => draftSettings || {}}
        onApply={handleApplySettings}
        title="Attribute Sum Bar Chart Settings"
        isApplyDisabled={!canCalculate || loading}
        applyButtonText={loading ? "Calculating..." : "Apply and Calculate"}
      >
        <AttributeSumBarChartSettings
          title={draftSettings?.title ?? title}
          attributeSource={draftSettings?.attributeSource ?? attributeSource}
          objectTypes={objectTypes}
          eventTypes={eventTypes}
          objectTypeNumericAttributes={objectTypeNumericAttributes}
          eventTypeNumericAttributes={eventTypeNumericAttributes}
          selectedObjectTypes={
            draftSettings?.selectedObjectTypes ?? selectedObjectTypes
          }
          selectedObjectAttributes={
            draftSettings?.selectedObjectAttributes ?? selectedObjectAttributes
          }
          selectedEventTypes={
            draftSettings?.selectedEventTypes ?? selectedEventTypes
          }
          selectedEventAttributes={
            draftSettings?.selectedEventAttributes ?? selectedEventAttributes
          }
          enabledGranularities={
            draftSettings?.enabledGranularities ?? enabledGranularities
          }
          onTitleChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), title: v }))
          }
          onAttributeSourceChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), attributeSource: v }))
          }
          onSelectedObjectTypesChange={(v) => {
            setDraftSettings((p) => ({ ...(p || {}), selectedObjectTypes: v }));
            fetchNumericAttributesForObjectTypes(v);
          }}
          onSelectedObjectAttributesChange={(v) =>
            setDraftSettings((p) => ({
              ...(p || {}),
              selectedObjectAttributes: v,
            }))
          }
          onSelectedEventTypesChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), selectedEventTypes: v }))
          }
          onSelectedEventAttributesChange={(v) =>
            setDraftSettings((p) => ({
              ...(p || {}),
              selectedEventAttributes: v,
            }))
          }
          onEnabledGranularitiesChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), enabledGranularities: v }))
          }
          dateRangeStart={dateRangeStart}
          dateRangeEnd={dateRangeEnd}
          onDateRangePresetChange={handleDateRangePresetChange}
          loading={loading}
        />
      </TileSettingsModal>
    </BaseTile>
  );
}

export default AttributeSumBarChart;
